import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { UseTransientScenariosReturn } from "../hooks/useTransientScenarios";

import { SCENARIO_COLUMN_CLASS_NAME } from "../lib/transientForm";
import {
  findReactiveParameter,
  findTryFile,
  REACTIVE_PARAMETERS,
  type ScenarioDefinitionFormState,
  TRY_FILES,
} from "../model/transientScenario";
import { TransientDropdownFieldRow } from "./TransientDropdownFieldRow";
import { TransientNumericFieldRow } from "./TransientNumericFieldRow";

type ScenarioDropdownColumnProps = {
  scenario: ScenarioDefinitionFormState;
  scenariosState: UseTransientScenariosReturn;
};

const SCENARIO_DROPDOWN_LABEL_CLASS_NAME = "w-[12rem]";

export const ScenarioDropdownColumn = ({ scenario, scenariosState }: ScenarioDropdownColumnProps) => {
  const { t } = useTranslation();
  const { scenarios, updateScenario, updateWeatherDataYear } = scenariosState;
  const isPrimaryWeatherYear = scenarios[0]?.id === scenario.id;
  const weatherDataYear = scenarios[0]?.weather_data_year ?? scenario.weather_data_year;
  const reactiveParameterOptions = useMemo(
    () =>
      REACTIVE_PARAMETERS.map((parameter) => ({
        id: parameter,
        label: t(`Execution.transient.scenario.reactiveParameter.${parameter}`),
      })),
    [t]
  );

  return (
    <div className={SCENARIO_COLUMN_CLASS_NAME}>
      <TransientDropdownFieldRow
        displayOptions={[...TRY_FILES]}
        label={t("Execution.transient.scenario.tryFile")}
        labelClassName={SCENARIO_DROPDOWN_LABEL_CLASS_NAME}
        onSelect={(value) => {
          const tryFile = findTryFile(value);
          if (tryFile) updateScenario(scenario.id, { try_file: tryFile });
        }}
        options={TRY_FILES}
        selectedValue={scenario.try_file}
      />
      <TransientDropdownFieldRow
        displayOptions={reactiveParameterOptions.map((option) => option.label)}
        label={t("Execution.transient.scenario.reactiveParameter.label")}
        labelClassName={SCENARIO_DROPDOWN_LABEL_CLASS_NAME}
        onSelect={(value) => {
          const reactiveParameter = findReactiveParameter(value);
          if (reactiveParameter) updateScenario(scenario.id, { reactive_parameter: reactiveParameter });
        }}
        options={REACTIVE_PARAMETERS}
        selectedValue={scenario.reactive_parameter}
      />
      <TransientNumericFieldRow
        disabled={!isPrimaryWeatherYear}
        label={t("Execution.transient.scenario.weatherDataYear")}
        labelClassName={SCENARIO_DROPDOWN_LABEL_CLASS_NAME}
        onChange={updateWeatherDataYear}
        step="1"
        value={weatherDataYear}
      />
      <TransientNumericFieldRow
        label={t("Execution.transient.configuration.requiredTMin")}
        labelClassName={SCENARIO_DROPDOWN_LABEL_CLASS_NAME}
        onChange={(requiredTMin) => updateScenario(scenario.id, { requiredTMin })}
        value={scenario.requiredTMin}
      />
    </div>
  );
};
